import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from 'context/auth';
import { Box, Button, FormControl, FormLabel, Heading, Input, Text, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Textarea, Divider, useToast, Checkbox, CheckboxGroup, Stack, VStack, HStack } from '@chakra-ui/react';
import { config, mfrProcessSelectables, subSystemSelectables, materialSelectables, finishSelectables, prioritySelectables } from 'util/constants';
import { MCFFormsContext } from 'context/mcfforms';
import { CreatableSelect, Select } from "chakra-react-select";

export default function SubmitterForm(props) {
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const { MCFForms, getMCFForms } = useContext(MCFFormsContext);
    const subsystemInputRef = useRef(null);
    const mfrProcessInputRef = useRef(null);
    const priorityInputRef = useRef(null);
    const finishInputRef = useRef(null);
    const materialInputRef = useRef(null);
    const [checkBoxStates, setCheckBoxStates] = useState({checkBox1: false, checkBox2: false, checkBox3: false, checkBox4: false});
    const [sameFilePath, setSameFilePath] = useState(false);
    const [quantityValue, setQuantityValue] = useState(1);
    const [numberOfFiles, setNumberOfFiles] = useState([0]);
    const [filePathValues, setFilePathValues] = useState(['']);
    // TODO: Maybe make a service that runs on my Linux machine that will parse through all folders on RoboTigers folder
    // and send them to mdb to make suggestions when they are entering file path and name in the form

    const addBulkFileHandler = () => {
        setNumberOfFiles([...numberOfFiles, numberOfFiles.length]);
        if (sameFilePath) {
            setFilePathValues([...filePathValues, filePathValues[0]]);
        } else {
            setFilePathValues([...filePathValues, '']);
        }
    }

    const handleFilePathChange = (element, index) => {
        if (sameFilePath) {
            let items = [...filePathValues];
            items = items.map((e, i) => {
                return element.target.value;
            });
            setFilePathValues(items);
        } else {
            let items = [...filePathValues];
            let item = filePathValues[index];
            item = element.target.value;
            items[index] = item;
            setFilePathValues(items);
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        let formData = {};
        formData.subsystem = e.target.subsystem.value;
        formData.revision = e.target.revision.value;
        formData.quantity = e.target.quantity.value;
        formData.material = e.target.material.value;
        formData.mfrProcess = [];
        // Machine is a multi-select input so need to check if more than 1 is selected
        if (e.target.mfrprocess.length > 1) // If more than 1 is selected then the return value becomes RadioNodeList array
        {
            e.target.mfrprocess.forEach((option) => {
                formData.mfrProcess.push(option.value);
            })
        }
        else {
            formData.mfrProcess.push(e.target.mfrprocess.value);
        }
        formData.finish = e.target.finish.value;
        formData.priority = e.target.priority.value;
        formData.notes = [];
        formData.notes.push({
            timestamp: Date.now(),
            note: "Submitted for review"
        });
        if (e.target.notes.value)
        {
            formData.notes.push({ 
                timestamp: Date.now(),
                note: `${user.firstName} ${user.lastName.charAt(0)}: ${e.target.notes.value}`
            });
        }

        formData.googleId =  user.googleId;
        formData.firstName = user.firstName;
        formData.lastName = user.lastName;
        let fileNames = [];
        numberOfFiles.forEach((element) => {
            formData.filePath = e.target[`filePath${element}`].value;
            formData.fileName = e.target[`fileName${element}`].value;
            fileNames.push(formData.fileName);
            if (formData.filePath && formData.fileName)
            {
                fetch(`/transactions/submitMCFForm`, {
                    method: 'POST',
                    // We convert the React state to JSON and send it as the POST body
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                }).then(response => {
                    if (response.status !== 200) {
                        throw `${response.status}: ${response.statusText}`
                    }
                    return response.text();          
                }).then(function(data) {
                    // Success
                    getMCFForms(() => {
                        setTimeout(() => {
                            subsystemInputRef.current.setValue('');
                            mfrProcessInputRef.current.setValue('');
                            priorityInputRef.current.setValue('');
                            finishInputRef.current.setValue('');
                            materialInputRef.current.setValue('');
                            setQuantityValue(1);
                            setNumberOfFiles([0]);
                            setSameFilePath(false);
                            setFilePathValues(['']);
                            setCheckBoxStates({checkBox1: false, checkBox2: false, checkBox3: false, checkBox4: false});
                            toast({
                                title: `${fileNames[element]} Submitted`,
                                status: 'success',
                                duration: 5000,
                                isClosable: true,
                            });
                            e.target.reset();
                            setLoading(false);
                        }, 500);
                    });
                })
                .catch(err => {
                    setLoading(false);
                    console.log(err);
                    toast({
                        title: `${fileNames[element]} Error`,
                        description: 'There was an error processing your request',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                });
            }
        })
    }
    
    useEffect(() => {
        if (user.admin || user.mentor)
        {
            setCheckBoxStates({checkBox1: true, checkBox2: true, checkBox3: true, checkBox4: true});
        }
    }, []);

    return (
        <>
            <Box margin={'0 auto'} mt={"1em"} width={{ base: '85%', md: '66%', lg: '50%' }}
                p={4}
                borderWidth="1px"
                rounded="lg"
                shadow="1px 1px 3px rgba(0,0,0,0.3)"
                bgColor={'#fff'}>
                <Heading mb={2} as="h1" size="lg" textAlign="center">
                    Part MCF Form
                </Heading>
                <Divider />
                <form onSubmit={handleSubmit} autoComplete="off">
                <FormControl isRequired mt={2}>
                        <FormLabel>Subsystem</FormLabel>
                        <Select ref={subsystemInputRef} required placeholder="Select Subsystem..." name="subsystem" size="sm"
                        options={subSystemSelectables}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <HStack>
                            <FormLabel>File Path</FormLabel>
                            <Button size='xs' onClick={addBulkFileHandler}>Add File</Button>
                            <Checkbox isRequired={false} isChecked={sameFilePath} onChange={() => {
                                setSameFilePath(!sameFilePath);
                                let items = [...filePathValues];
                                let findText = Math.max(0, filePathValues.findIndex(element => element.length > 0));
                                items = items.map((e, i) => {
                                    return filePathValues[findText];
                                });
                                setFilePathValues(items);
                                }}>Same File Paths</Checkbox>
                        </HStack>
                        <VStack>
                            {numberOfFiles.map((e, i) => {
                                let name = `filePath${e}`;
                                if (i === 0) {
                                    return <HStack key={e} w='full'><Text>{e + 1}.</Text><Input name={name} type="text" value={ filePathValues[e] } onChange={ (element) => handleFilePathChange(element, e) } size="sm"/></HStack>
                                } else {
                                    return <HStack key={e} w='full'><Text>{e + 1}.</Text><Input name={name} type="text" value={ filePathValues[e] } onChange={ (element) => handleFilePathChange(element, e) } size="sm"/></HStack>
                                }
                            })}
                        </VStack>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>File Name</FormLabel>
                        <VStack>
                            {numberOfFiles.map((e) => {
                                let name = `fileName${e}`;
                                return <HStack key={e} w='full'><Text>{e + 1}.</Text><Input name={name} type="text" size="sm"/></HStack>
                            })}
                        </VStack>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Revision</FormLabel>
                        <Input name="revision" type="text" size="sm"/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Quantity</FormLabel>
                        <NumberInput name="quantity" value={quantityValue} onChange={e => { setQuantityValue(
                        // @ts-ignore
                        e); }} defaultValue={1} min={1} max={9001}>
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Material</FormLabel>
                        <CreatableSelect ref={materialInputRef} required name="material" placeholder="Select Material..." size="sm"
                        options={materialSelectables}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Machine(s)</FormLabel>
                        <Select ref={mfrProcessInputRef} required isMulti placeholder="Select Machine(s)..." name="mfrprocess"  size="sm"
                        options={mfrProcessSelectables}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Finish</FormLabel>
                        <CreatableSelect ref={finishInputRef} required name="finish" placeholder="Select Finish..." size="sm"
                        options={finishSelectables}
                        />
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Priority</FormLabel>
                        <Select ref={priorityInputRef} defaultValue={{label: "Medium", value: "3" }} placeholder="Select Priority..." required name="priority"  size="sm"
                        options={prioritySelectables}/>
                    </FormControl>
                    <FormControl mt={2}>
                        <FormLabel>Notes</FormLabel>
                        <Textarea name="notes"
                            placeholder='Notes'
                            size='md'
                        />
                    </FormControl>
                    <CheckboxGroup colorScheme='green'>
                    <Stack mt={4} spacing={[1, 2]} direction={['column']}>
                        <Checkbox isChecked={checkBoxStates.checkBox1} onChange={e => {setCheckBoxStates({...checkBoxStates, checkBox1: !checkBoxStates.checkBox1})}} required>I checked the machining considerations for the material & machine being used.</Checkbox>
                        <Checkbox isChecked={checkBoxStates.checkBox2} onChange={e => {setCheckBoxStates({...checkBoxStates, checkBox2: !checkBoxStates.checkBox2})}} required>The part is constrained and based on Origin.</Checkbox>
                        <Checkbox isChecked={checkBoxStates.checkBox3} onChange={e => {setCheckBoxStates({...checkBoxStates, checkBox3: !checkBoxStates.checkBox3})}} required>My part is named with the proper convention and iProperties complete.</Checkbox>
                        <Checkbox isChecked={checkBoxStates.checkBox4} onChange={e => {setCheckBoxStates({...checkBoxStates, checkBox4: !checkBoxStates.checkBox4})}} required> I designed my part with Brandon's Best Practices (BBP) in mind.</Checkbox>
                    </Stack>
                    </CheckboxGroup>
                    <Button
                        isLoading={loading}
                        name="submitButton"
                        loadingText='Submitting'
                        colorScheme="teal"
                        type="submit"
                        width="full"
                        mt={4}
                    >
                        Submit
                    </Button>
                </form>
            </Box>
        </>
    );
}
